import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiClient } from '../api/apiConfig';
import { COLORS, SPACING, TYPOGRAPHY } from '../design/tokens';
import { Screen, Panel, Badge, Divider, Button } from '../design/primitives';
import { PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import DreamForm from '../components/forms/DreamForm';

interface Dream {
  id: number;
  title: string;
  description?: string;
  inspiration?: string;
  driver?: string;
  is_active?: boolean;
  category?: { id: number; name: string } | null;
  related_goals?: { id: number; title: string }[];
  created_at?: string;
  last_reflected_at?: string;
}

const DreamDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [dream, setDream] = useState<Dream | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditOpen, setIsEditOpen] = useState(false);

  useEffect(() => {
    const fetchDream = async () => {
      try {
        const response = await apiClient.get(`/api/dreams/${id}/`);
        setDream(response.data);
      } catch (err) {
        setError('Failed to load dream details.');
      } finally {
        setLoading(false);
      }
    };
    fetchDream();
  }, [id]);

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this dream?')) return;
    try {
      await apiClient.delete(`/api/dreams/${id}/`);
      navigate('/dreams');
    } catch (err) {
      setError('Failed to delete dream.');
    }
  };

  const handleDreamUpdated = (updatedDream: Dream) => {
    setDream(updatedDream);
    setIsEditOpen(false);
  };

  if (loading) {
    return (
      <Screen>
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '50vh',
        }}>
          <p style={{
            fontFamily: TYPOGRAPHY.body.fontFamily,
            fontSize: TYPOGRAPHY.body.sizes.default,
            color: COLORS.text.secondary,
          }}>
            Loading...
          </p>
        </div>
      </Screen>
    );
  }

  if (error || !dream) {
    return (
      <Screen>
        <Panel>
          <p style={{
            fontFamily: TYPOGRAPHY.body.fontFamily,
            fontSize: TYPOGRAPHY.body.sizes.default,
            color: COLORS.status.error,
            margin: 0,
          }}>
            {error || 'Dream not found.'}
          </p>
        </Panel>
      </Screen>
    );
  }

  return (
    <Screen>
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        <Panel>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
          }}>
            <h1 style={{
              fontFamily: TYPOGRAPHY.headings.fontFamily,
              fontSize: TYPOGRAPHY.headings.sizes.h1,
              fontWeight: TYPOGRAPHY.headings.weight,
              color: COLORS.text.primary,
              margin: 0,
              marginBottom: SPACING[8],
            }}>
              {dream.title}
            </h1>
            <div style={{ display: 'flex', gap: SPACING[8] }}>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsEditOpen(true)}
                title="Edit dream"
              >
                <PencilIcon style={{ width: '16px', height: '16px' }} />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleDelete}
                title="Delete dream"
              >
                <TrashIcon style={{ width: '16px', height: '16px' }} />
              </Button>
            </div>
          </div>

          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: SPACING[8],
            marginBottom: SPACING[16],
          }}>
            <Badge color={dream.is_active ? 'success' : 'default'}>
              {dream.is_active ? 'Active' : 'Inactive'}
            </Badge>
            {dream.category && (
              <Badge color="accent">{dream.category.name}</Badge>
            )}
          </div>

          <Divider />

          {dream.description && (
            <div style={{ marginTop: SPACING[16], marginBottom: SPACING[16] }}>
              <h3 style={{
                fontFamily: TYPOGRAPHY.headings.fontFamily,
                fontSize: TYPOGRAPHY.body.sizes.default,
                fontWeight: '600',
                color: COLORS.text.primary,
                margin: 0,
                marginBottom: SPACING[8],
              }}>Description</h3>
              <p style={{
                fontFamily: TYPOGRAPHY.body.fontFamily,
                fontSize: TYPOGRAPHY.body.sizes.default,
                color: COLORS.text.secondary,
                margin: 0,
                lineHeight: '1.6',
                whiteSpace: 'pre-wrap',
              }}>
                {dream.description}
              </p>
            </div>
          )}

          {dream.inspiration && (
            <div style={{ marginBottom: SPACING[16] }}>
              <h3 style={{
                fontFamily: TYPOGRAPHY.headings.fontFamily,
                fontSize: TYPOGRAPHY.body.sizes.default,
                fontWeight: '600',
                color: COLORS.text.primary,
                margin: 0,
                marginBottom: SPACING[8],
              }}>Inspiration</h3>
              <p style={{
                fontFamily: TYPOGRAPHY.body.fontFamily,
                fontSize: TYPOGRAPHY.body.sizes.default,
                color: COLORS.text.secondary,
                margin: 0,
                lineHeight: '1.6',
                whiteSpace: 'pre-wrap',
              }}>
                {dream.inspiration}
              </p>
            </div>
          )}

          {dream.driver && (
            <div style={{ marginBottom: SPACING[16] }}>
              <h3 style={{
                fontFamily: TYPOGRAPHY.headings.fontFamily,
                fontSize: TYPOGRAPHY.body.sizes.default,
                fontWeight: '600',
                color: COLORS.text.primary,
                margin: 0,
                marginBottom: SPACING[8],
              }}>Driver</h3>
              <p style={{
                fontFamily: TYPOGRAPHY.body.fontFamily,
                fontSize: TYPOGRAPHY.body.sizes.default,
                color: COLORS.text.secondary,
                margin: 0,
                lineHeight: '1.6',
                whiteSpace: 'pre-wrap',
              }}>
                {dream.driver}
              </p>
            </div>
          )}

          {dream.related_goals && dream.related_goals.length > 0 && (
            <>
              <Divider />
              <div style={{ marginTop: SPACING[16] }}>
                <h3 style={{
                  fontFamily: TYPOGRAPHY.headings.fontFamily,
                  fontSize: TYPOGRAPHY.body.sizes.default,
                  fontWeight: '600',
                  color: COLORS.text.primary,
                  margin: 0,
                  marginBottom: SPACING[8],
                }}>Related Goals</h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: SPACING[8] }}>
                  {dream.related_goals.map(goal => (
                    <Link
                      key={goal.id}
                      to={`/goals/${goal.id}`}
                      style={{ textDecoration: 'none' }}
                    >
                      <Badge color="accent">{goal.title}</Badge>
                    </Link>
                  ))}
                </div>
              </div>
            </>
          )}

          <Divider />

          <div style={{
            display: 'flex',
            gap: SPACING[16],
            marginTop: SPACING[16],
          }}>
            {dream.created_at && (
              <p style={{
                fontSize: TYPOGRAPHY.body.sizes.tiny,
                color: COLORS.text.tertiary,
                margin: 0,
              }}>
                Created: {new Date(dream.created_at).toLocaleDateString()}
              </p>
            )}
            {dream.last_reflected_at && (
              <p style={{
                fontSize: TYPOGRAPHY.body.sizes.tiny,
                color: COLORS.text.tertiary,
                margin: 0,
              }}>
                Last reflected: {new Date(dream.last_reflected_at).toLocaleDateString()}
              </p>
            )}
          </div>
        </Panel>
      </div>

      <DreamForm
        isOpen={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        onDreamCreated={handleDreamUpdated}
        initialDream={dream}
        isEditMode={true}
      />
    </Screen>
  );
};

export default DreamDetailPage;
